import { BaseEdge, MarkerType, type Position } from '@xyflow/react';
import { type FlowContact } from '../types';
import { ContactFlowVisualizer } from '../components/ContactFlowVisualizer';
import { type BranchEdgeData } from '../types/edges';

// Uniform gray color for all branch edges (cleaner visual)
const BRANCH_EDGE_COLOR = '#9CA3AF'; // Gray-400
const BRANCH_EDGE_COLOR_SELECTED = '#6B7280'; // Gray-500

/**
 * Creates an orthogonal (right-angle) path for branch edges.
 *
 * For vertically aligned nodes (within the same branch lane):
 * - Uses a straight vertical line for clean appearance
 *
 * For horizontally offset nodes (switch to branch start):
 * - Uses horizontal-first routing: drops down slightly from source,
 *   routes horizontally to align with target X, then drops down to target.
 * - This creates a clean "T" shape from the switch node spreading to branches.
 */
function createOrthogonalPath(sourceX: number, sourceY: number, targetX: number, targetY: number): string {
	// Check if source and target are vertically aligned (same X coordinate)
	// Use a small threshold to account for floating point precision
	const xDifference = Math.abs(sourceX - targetX);
	const VERTICAL_ALIGNMENT_THRESHOLD = 5;

	if (xDifference < VERTICAL_ALIGNMENT_THRESHOLD) {
		// Nodes are vertically aligned - use straight vertical line
		// This creates clean edges within branch lanes
		return `M ${sourceX} ${sourceY} L ${targetX} ${targetY}`;
	}

	// Nodes are horizontally offset - use orthogonal routing
	// Use midpoint Y for horizontal routing channel
	// This creates a cleaner tree structure with more vertical separation
	const routingChannelY = (sourceY + targetY) / 2;

	// Create path with clean right angles:
	// 1. Drop down from source to routing channel
	// 2. Route horizontally to target X
	// 3. Drop down to target
	return `M ${sourceX} ${sourceY} L ${sourceX} ${routingChannelY} L ${targetX} ${routingChannelY} L ${targetX} ${targetY}`;
}

interface BranchEdgeProps {
	id: string;
	sourceX: number;
	sourceY: number;
	targetX: number;
	targetY: number;
	sourcePosition: Position;
	targetPosition: Position;
	selected?: boolean;
	data?: BranchEdgeData & {
		activeContacts?: FlowContact[];
	};
	source: string;
	target: string;
}

export function BranchEdge(props: BranchEdgeProps) {
	const {
		id,
		sourceX,
		sourceY,
		targetX,
		targetY,
		sourcePosition,
		targetPosition,
		selected,
		data,
		source: _source,
		target: _target,
	} = props;

	const activeContacts = data?.activeContacts || [];
	const isDefault = data?.isDefault ?? false;

	// Use orthogonal (right-angle) path for clean branch edges
	const edgePath = createOrthogonalPath(sourceX, sourceY, targetX, targetY);

	const edgeAriaLabel = `Branch connection${isDefault ? ' (default)' : ''}${activeContacts.length > 0 ? `, ${activeContacts.length} contacts taking this path` : ''}`;

	return (
		<>
			<g role='img' aria-label={edgeAriaLabel}>
				<BaseEdge
					id={id}
					path={edgePath}
					markerEnd={MarkerType.ArrowClosed}
					style={{
						stroke: selected ? BRANCH_EDGE_COLOR_SELECTED : BRANCH_EDGE_COLOR,
						strokeWidth: selected ? 3 : 2,
						strokeDasharray: isDefault ? '5,5' : 'none',
					}}
				/>
			</g>
			{activeContacts.length > 0 && (
				<ContactFlowVisualizer
					edgeId={id}
					sourceX={sourceX}
					sourceY={sourceY}
					targetX={targetX}
					targetY={targetY}
					sourcePosition={sourcePosition}
					targetPosition={targetPosition}
					activeContacts={activeContacts}
				/>
			)}
		</>
	);
}
